import CryptoJS from 'crypto-js';
import { MASTER_KEY, X_HEADER_ID_NAME } from '@utils/constants';
import { msToDateWithTimezone } from '.';
import { logger } from '@utils/logger';

export const STORAGE_KEY = 'ms';
export const DATE_COUNTER_UPDATED = 'dcu';

const getStorageCounters = (): Record<string, number> => {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return {};
  try {
    const decrypted = decrypt(raw);
    return typeof decrypted === 'object' ? decrypted : {};
  } catch (e) {
    return {};
  }
};

const setStorageCounters = (counters: Record<string, number>) => {
  localStorage.setItem(STORAGE_KEY, encrypt(counters));
};

const countedSegments: Record<string, Set<string>> = {};
const countedRides: Record<string, Set<string>> = {};

export type ModuleType =
  | 'payor'
  | 'member'
  | 'user'
  | 'contractor'
  | 'place'
  | 'ride'
  | 'standingOrder'
  | 'byModuleId';

export const initializeTabId = (): string => {
  const ssId = sessionStorage.getItem('tab_instance_id');
  const winId = window.name;

  if (!ssId || !winId || ssId !== winId) {
    const newId = `tab_${crypto.randomUUID()}`;
    sessionStorage.setItem('tab_instance_id', newId);
    window.name = newId;
    return newId;
  }
  return ssId;
};

export const CURRENT_TAB_ID = initializeTabId();
export const getTabId = () => CURRENT_TAB_ID;

export const encrypt = (data: any) => {
  if (!data) return '';
  const jsonStr = JSON.stringify(data);
  return CryptoJS.AES.encrypt(jsonStr, MASTER_KEY)
    .toString()
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

export const decrypt = (cipher: any) => {
  const rawCipher = typeof cipher === 'string' ? cipher : cipher?.byModuleId;
  if (!rawCipher || typeof rawCipher !== 'string') return null;
  try {
    const base64 = rawCipher
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(rawCipher.length + ((4 - (rawCipher.length % 4)) % 4), '=');
    const bytes = CryptoJS.AES.decrypt(base64, MASTER_KEY);
    const decryptedStr = bytes.toString(CryptoJS.enc.Utf8);
    return decryptedStr ? JSON.parse(decryptedStr) : null;
  } catch (err) {
    return null;
  }
};

export const sessionSetter = {
  setItem: (key: string, value: unknown) => {
    const encryptedValue = encrypt(JSON.stringify(value));
    window.sessionStorage.setItem(key, encryptedValue);
  },

  getItem: <T>(key: string): T | null => {
    const item = window.sessionStorage.getItem(key);
    if (!item) return null;

    try {
      const decrypted = decrypt(item);
      return typeof decrypted === 'string' ? JSON.parse(decrypted) : decrypted;
    } catch (e) {
      return null;
    }
  },

  removeItem: (key: string) => {
    window.sessionStorage.removeItem(key);
  },

  clearAll: () => {
    window.sessionStorage.clear();
  },
};

export const updateXHeaderId = (key: string, value: number | string | null) => {
  const current =
    sessionSetter.getItem<Record<string, any>>(X_HEADER_ID_NAME) || {};
  const updated = { ...current };
  if (value === null) delete updated[key];
  else updated[key] = value;
  sessionSetter.setItem(X_HEADER_ID_NAME, updated);
};

export const tabNameSetter = {
  setTabName: (v: string) =>
    sessionStorage.setItem('tabName', JSON.stringify(v)),
  getTabName: () => JSON.parse(sessionStorage.getItem('tabName') || 'null'),
  removeTabName: () => sessionStorage.removeItem('tabName'),
};

export function incrementAndCleanFromWebhookMs(
  webhookMs: number,
  timeZone: string,
  segmentId: string
) {
  const todayKey = msToDateWithTimezone(webhookMs, timeZone);
  const todayMs = new Date(todayKey).getTime();
  const counters = getStorageCounters();

  Object.keys(counters).forEach((key) => {
    if (new Date(key).getTime() < todayMs) {
      delete counters[key];
      delete countedSegments[key];
    }
  });

  if (!countedSegments[todayKey]) countedSegments[todayKey] = new Set<string>();
  if (!countedSegments[todayKey].has(segmentId)) {
    counters[todayKey] = (counters[todayKey] ?? 0) + 1;
    countedSegments[todayKey].add(segmentId);
    setStorageCounters(counters);
    window.dispatchEvent(
      new CustomEvent(DATE_COUNTER_UPDATED, { detail: { date: todayKey } })
    );
  }
}

export function incrementAndCleanFromDateString(
  dateString: string,
  rideId: string
) {
  const todayMs = new Date(dateString).getTime();

  const counters = getStorageCounters();

  Object.keys(counters).forEach((key) => {
    if (new Date(key).getTime() < todayMs) {
      delete counters[key];
      if (countedRides[key]) {
        delete countedRides[key];
      }
    }
  });

  if (!countedRides[dateString]) {
    countedRides[dateString] = new Set<string>();
  }

  if (!countedRides[dateString].has(rideId)) {
    counters[dateString] = (counters[dateString] ?? 0) + 1;
    countedRides[dateString].add(rideId);

    setStorageCounters(counters);

    window.dispatchEvent(
      new CustomEvent(DATE_COUNTER_UPDATED, { detail: { date: dateString } })
    );
  }
}

export function clearAllCounters() {
  localStorage.removeItem(STORAGE_KEY);
  Object.keys(countedSegments).forEach((k) => delete countedSegments[k]);
  window.dispatchEvent(
    new CustomEvent(DATE_COUNTER_UPDATED, { detail: { date: null } })
  );
}

export const openSecureTab = (url: string, data: any) => {
  const newWindow = window.open(url, '_blank');
  const targetOrigin = window.location.origin;

  if (!newWindow) {
    logger.error('Failed to open new secure window.');
    return;
  }

  const syncData = (event: MessageEvent) => {
    if (event.origin !== targetOrigin) return;

    if (event.data.type === 'REQUEST_SECURE_DATA') {
      newWindow.postMessage(
        {
          type: 'SECURE_DATA_RESPONSE',
          payload: data,
        },
        targetOrigin
      );

      window.removeEventListener('message', syncData);
    }
  };

  window.addEventListener('message', syncData);

  const checkClosed = setInterval(() => {
    if (newWindow.closed) {
      window.removeEventListener('message', syncData);
      clearInterval(checkClosed);
    }
  }, 1000);
};
